/**
 * Knowledge Files registry — the agent-level "documents the agent can read"
 * feature. A knowledge file is a Bishop asset whose `reference_uri`
 * (`bishop://{id}--{filename}`) is registered on the target agent so:
 *   1. every end-user thread lists it in the agent's context, and
 *   2. the agent can read it on demand via the `artifacts` tool source's
 *      `artifact_read` (which resolves a `bishop://` uri verbatim).
 *
 * The registry lives in a dedicated `text` memory block named "Knowledge Files"
 * (distinct from the free-text "Memory" block). Its content is a short note
 * followed by one line per file: `- <displayName> (<reference_uri>)`. Chat
 * fulfillment (attach/remove/list) and the Preview pane both read and write
 * through the helpers here so the two edit paths stay in sync.
 *
 * NOTE: this module deliberately does NOT import from `use-agent-builder-api`
 * (which imports back from here) — the block type is inlined as the `'text'`
 * literal to avoid an import cycle that could leave the constant `undefined`.
 */

/**
 * `config.name` of the registry block. A distinct name (not "Memory") keeps it
 * from colliding with the free-text Memory `text` block — `upsertMemoryBlock`
 * matches `text` blocks by (type, name).
 */
export const KNOWLEDGE_FILES_BLOCK_NAME = 'Knowledge Files';

/**
 * Block type of the registry — a plain `text` block. Matches
 * `MEMORY_BLOCK_TYPE.memory` ('text') but inlined to avoid an import cycle
 * (see the module note above).
 */
export const KNOWLEDGE_FILES_BLOCK_TYPE = 'text';

/**
 * Header prepended to the block content. Tells the agent what the list is and
 * how to read an entry (via `artifact_read` on the `bishop://` uri).
 */
export const KNOWLEDGE_FILES_HEADER =
  'Reference documents available to you — read one with artifact_read using its bishop:// uri:';

/** One attached knowledge file: a display name + its Bishop reference uri. */
export interface IKnowledgeFile {
  name: string;
  uri: string;
}

/**
 * Parse the "Knowledge Files" block content into the list of attached files.
 * Each file is stored as `- <name> (<uri>)`; the header (and any stray lines)
 * are ignored. Tolerant of extra whitespace so a hand-edited block still parses.
 */
export function parseKnowledgeFiles(
  content: string | undefined,
): IKnowledgeFile[] {
  if (!content) return [];
  const files: IKnowledgeFile[] = [];
  for (const rawLine of content.split('\n')) {
    const line = rawLine.trim();
    if (!line.startsWith('- ')) continue;
    // `- <name> (<uri>)` — the uri is the last parenthesized group so a name
    // that itself contains parentheses still parses.
    const match = /^-\s*(.*?)\s*\(([^()]*)\)\s*$/.exec(line);
    if (!match) continue;
    const name = match[1].trim();
    const uri = match[2].trim();
    if (!uri) continue;
    files.push({ name: name || uri, uri });
  }
  return files;
}

/**
 * Serialize a file list back into block content: the header followed by one
 * `- <name> (<uri>)` line per file. An empty list yields the header alone so
 * the block still explains itself to the agent.
 */
export function serializeKnowledgeFiles(files: IKnowledgeFile[]): string {
  const lines = files.map(f => `- ${f.name} (${f.uri})`);
  return [KNOWLEDGE_FILES_HEADER, ...lines].join('\n');
}

/**
 * Merge new files into an existing list, de-duplicating by `uri` (a file
 * already registered is left as-is rather than duplicated). Returns the merged
 * list preserving existing order, with genuinely-new files appended.
 */
export function mergeKnowledgeFiles(
  existing: IKnowledgeFile[],
  incoming: IKnowledgeFile[],
): IKnowledgeFile[] {
  const seen = new Set(existing.map(f => f.uri));
  const merged = [...existing];
  for (const file of incoming) {
    if (!file.uri || seen.has(file.uri)) continue;
    seen.add(file.uri);
    merged.push(file);
  }
  return merged;
}

/**
 * Drop the file whose display name matches `name` (case-insensitive). Falls
 * back to matching on `uri` so a caller holding a uri can remove by it too.
 * Returns the filtered list (unchanged if nothing matched).
 */
export function removeKnowledgeFile(
  files: IKnowledgeFile[],
  name: string,
): IKnowledgeFile[] {
  const needle = name.trim().toLowerCase();
  return files.filter(
    f => f.name.toLowerCase() !== needle && f.uri.toLowerCase() !== needle,
  );
}
